import type { MarketingBannersTranslations } from "./types";

import { useIsMobile } from "../hooks";
import { InputV2 } from "../InputV2";
import { ResponsiveModal } from "../ResponsiveModal";
import { RenameBannerFooter } from "./RenameBannerFooter";
import { RENAME_CHAR_LIMIT } from "./types";
import { useAutoFocus } from "./useAutoFocus";

type RenameBannerModalProps = {
  name: string;
  error: string;
  isSaving: boolean;
  isSaveDisabled: boolean;
  translations: MarketingBannersTranslations;
  onClose: () => void;
  /**
   * Fired when the footer's Cancel button specifically is clicked — distinct
   * from `onClose`, which `ResponsiveModal` also invokes for backdrop/X-button
   * dismissal. Defaults to `onClose` when omitted, so existing callers keep
   * their current behaviour.
   */
  onCancel?: () => void;
  onNameChange: (value: string) => void;
  onSave: () => void;
};

export const RenameBannerModal = ({
  name,
  error,
  isSaving,
  isSaveDisabled,
  translations,
  onClose,
  onCancel,
  onNameChange,
  onSave,
}: RenameBannerModalProps) => {
  const isMobile = useIsMobile();
  const inputRef = useAutoFocus<HTMLInputElement>();

  return (
    <ResponsiveModal
      isOpen
      onClose={onClose}
      isMobile={isMobile}
      title={translations.rename.title}
      footer={
        <RenameBannerFooter
          isSaving={isSaving}
          isSaveDisabled={isSaveDisabled}
          translations={translations}
          onCancel={onCancel ?? onClose}
          onSave={onSave}
        />
      }
      maxWidth={480}
      contentOverflow="visible"
    >
      <InputV2
        ref={inputRef}
        name="rename-banner"
        value={name}
        characterLimit={RENAME_CHAR_LIMIT}
        maxLength={RENAME_CHAR_LIMIT}
        onChange={onNameChange}
        error={error}
        showError={!!error}
      />
    </ResponsiveModal>
  );
};
